import { ARGENTINA_TIME_ZONE } from "constants/index";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { Container, User } from "types";

dayjs.extend(utc);
dayjs.extend(timezone);

export const dateISOFormat = (date: string | null) => {
  if (date) {
    try {
      return new Date(date).toISOString().slice(0, 19).replace("T", " ");
    } catch (dateExcep) {
      return null;
    }
  } else {
    return null;
  }
};

export const volumeCalc = (
  width: number | undefined,
  long: number | undefined,
  height: number | undefined,
) => {
  if (!width || !long || !height) {
    return 0;
  }
  return width * long * height;
};

export const surfaceCalc = (
  width: number | undefined,
  long: number | undefined,
) => {
  if (!width || !long) {
    return 0;
  }
  return width * long;
};

const CONTAINER_VOLUME_WEIGHT_FACTOR = 350;

export const calculateContainerVolumeWeight = (
  m3Max: number | string | undefined | null,
) => {
  const normalizedValue = toNumber(m3Max ?? 0);

  if (!normalizedValue) {
    return 0;
  }

  return roundToThree(normalizedValue * CONTAINER_VOLUME_WEIGHT_FACTOR);
};

export const isSuggestedContainer = (
  container: Container,
  volume: number | undefined,
) => {
  if (!container.is_active || !volume) {
    return false;
  }

  const m3Min = toNumber(container.m3_min ?? 0);
  const m3Max = toNumber(container.m3_max ?? 0);

  return volume >= m3Min && volume <= m3Max;
};

export const getSuggestedContainers = (
  containers: Container[],
  volume: number | undefined,
) => containers.filter((container) => isSuggestedContainer(container, volume));

export const arsFormatter = (amount: number | bigint) =>
  Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(
    amount,
  );

export const formatTo2Decimals = (amount: number | bigint) =>
  amount.toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const serialToDateTime = (serialString: string): string => {
  const serial = parseInt(serialString, 10);

  const baseDate = new Date(Date.UTC(1900, 0, 1));
  const adjustedDate = new Date(
    baseDate.getTime() + (serial - 2) * 24 * 60 * 60 * 1000,
  );

  adjustedDate.setUTCHours(3, 0, 0, 0);

  const year = adjustedDate.getUTCFullYear();
  const month = String(adjustedDate.getUTCMonth() + 1).padStart(2, "0");
  const day = String(adjustedDate.getUTCDate()).padStart(2, "0");
  const hours = String(adjustedDate.getUTCHours()).padStart(2, "0");
  const minutes = String(adjustedDate.getUTCMinutes()).padStart(2, "0");
  const seconds = String(adjustedDate.getUTCSeconds()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export const normalize = (val: unknown): string => {
  return (val || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
};

export const getNestedValue = <T extends object>(
  obj: T,
  path: string,
): unknown =>
  path.split(".").reduce((acc: any, part: string) => acc?.[part], obj);

export const getUserFullName = (
  id: number | null | undefined,
  users: User[],
): string => {
  const user = users.find((u) => u.id === id);
  return user ? `${user.first_name} ${user.last_name}` : "";
};

export const normStr = (v?: string | null) => (v ?? "").trim() || null;
export const normDate = (v?: string | null) => (v ? v.split("T")[0] : null);
export const normNum = (v: any) =>
  v === "" || v == null || isNaN(Number(v)) ? null : Number(v);

export const formatDateOnlyDMY = (dateStr?: string | null): string => {
  if (!dateStr) return "";
  const datePart = dateStr.split(/[T ]/)[0];
  const [y, m, d] = datePart.split("-");
  return `${d}/${m}/${y}`;
};

export const toDateValue = (value?: string | null) =>
  value ? new Date(`${value}T00:00:00`) : null;

export function sanitizeExcelValue<
  T extends string | number | null | undefined,
>(value: T): T {
  if (typeof value === "string") {
    return value.trim() as T;
  }
  return value;
}

export const normalizeCellString = (value: unknown) => {
  const sanitized = sanitizeExcelValue(
    value as string | number | null | undefined,
  );

  if (sanitized == null) return null;

  const normalized = String(sanitized).trim();
  return normalized === "" ? null : normalized;
};

export const isSerialDateValue = (value: string) => /^\d+(\.0+)?$/.test(value);

export const extractDateParts = (value: string) => {
  const trimmed = value.trim();

  if (isSerialDateValue(trimmed)) {
    return serialToDateTime(trimmed).slice(0, 10);
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    return trimmed.slice(0, 10);
  }

  const dmyMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);

  if (dmyMatch) {
    const [, day, month, year] = dmyMatch;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  throw new Error(`La fecha "${value}" no tiene un formato valido.`);
};

export const toIsoDateTime = (value: unknown, fieldName: string) => {
  const normalized = normalizeCellString(value);

  if (!normalized) {
    throw new Error(`El campo "${fieldName}" es obligatorio.`);
  }

  const datePart = extractDateParts(normalized);
  return `${datePart}T03:00:00.000Z`;
};

export const toDateOnly = (value: unknown) => {
  const normalized = normalizeCellString(value);

  if (!normalized) {
    return null;
  }

  return extractDateParts(normalized);
};

export const toDateString = (value?: string | Date | null) => {
  if (!value) return null;
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value).slice(0, 10);
};

export const normalizeImportValue = (value: unknown) =>
  normalize(sanitizeExcelValue(String(value ?? ""))).trim();

export const isSameImportValue = (left: unknown, right: unknown) =>
  normalizeImportValue(left) === normalizeImportValue(right);

export function normalizeExcel(value: any) {
  return sanitizeExcelValue(String(value ?? ""))
    .trim()
    .toLowerCase();
}

export const roundToThree = (value: number): number => {
  if (isNaN(value)) return 0;
  return Math.round((value + Number.EPSILON) * 1000) / 1000;
};

export const toNumber = (v: string | number): number => {
  if (typeof v === "number") return v;
  const s = (v ?? "").toString().trim().replace(",", ".");
  const n = Number.parseFloat(s);
  return Number.isNaN(n) ? 0 : n;
};

export const round = (n: number, decimals = 3) => {
  const f = Math.pow(10, decimals);
  return Math.round(n * f) / f;
};

export const dateToMySQLFormat = (date: string | Date | null): string => {
  if (!date) return "";
  return dayjs(date).tz(ARGENTINA_TIME_ZONE).format("YYYY-MM-DD HH:mm:ss");
};

export const dateToLocalMySQLFormat = (date: string | Date | null): string => {
  if (!date) return "";
  return dayjs(date).format("YYYY-MM-DD HH:mm:ss");
};

export function normalizeHeader(value: any): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function normalizeSchemaKeys<T extends Record<string, any>>(schema: T) {
  const normalized: Record<string, any> = {};

  for (const key in schema) {
    normalized[normalizeHeader(key)] = schema[key];
  }

  return normalized;
}

export const formatObservationValue = (value?: string | null) => {
  if (value == null) return "-";

  const trimmed = value.trim();
  return trimmed.length ? trimmed : "-";
};

export const formatUser = (
  log: {
    user?: {
      id: number;
      first_name?: string;
      last_name?: string;
      email?: string;
    } | null;
    user_id?: number | null;
  },
) => {
  if (log.user) {
    const fullName = `${log.user.first_name ?? ""} ${log.user.last_name ?? ""}`.trim();

    if (fullName) return fullName;
    if (log.user.email) return log.user.email;
    return `Usuario #${log.user.id}`;
  }

  if (log.user_id != null) return `Usuario #${log.user_id}`;

  return "-";
};


